import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Calculator, Library, FileCheck, BookOpen, Search, Check, ArrowRight, Percent, DollarSign,
} from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { glossary, documentsChecklist, buyerGuideSteps, company } from "../mock";

const tabs = [
  { key: "calculators", label: "Calculators", icon: Calculator },
  { key: "guide", label: "Borrower's Guide", icon: BookOpen },
  { key: "glossary", label: "Glossary", icon: Library },
  { key: "documents", label: "Documents Needed", icon: FileCheck },
];

function currency(n) {
  if (!isFinite(n)) return "$0";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

const CALC = {
  DEFAULT_AMOUNT: 500000,
  DEFAULT_RATE: 7.5,
  DEFAULT_YEARS: 25,
  AMOUNT_MIN: 25000,
  AMOUNT_MAX: 100000000,
  AMOUNT_STEP: 25000,
  RATE_MIN: 1,
  RATE_MAX: 20,
  RATE_STEP: 0.125,
  YEARS_MIN: 1,
  YEARS_MAX: 30,
  YEARS_STEP: 1,
  MONTHS_PER_YEAR: 12,
  PERCENT_DIVISOR: 100,
};

function LoanCalculator() {
  const [amount, setAmount] = useState(CALC.DEFAULT_AMOUNT);
  const [rate, setRate] = useState(CALC.DEFAULT_RATE);
  const [years, setYears] = useState(CALC.DEFAULT_YEARS);

  const { monthly, totalInterest, totalPaid } = useMemo(() => {
    const p = Number(amount) || 0;
    const r = (Number(rate) || 0) / CALC.PERCENT_DIVISOR / CALC.MONTHS_PER_YEAR;
    const n = (Number(years) || 0) * CALC.MONTHS_PER_YEAR;
    if (p <= 0 || n <= 0) return { monthly: 0, totalInterest: 0, totalPaid: 0 };
    const m = r === 0 ? p / n : (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const total = m * n;
    return { monthly: m, totalInterest: total - p, totalPaid: total };
  }, [amount, rate, years]);

  const NumberField = ({ label, value, onChange, min, max, step, prefix, suffix }) => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-slate-700">{label}</label>
        <span className="text-sm font-bold text-[#0b1f3a]">
          {prefix}{Number(value).toLocaleString()}{suffix}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>}
          <input
            type="number"
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={(e) => onChange(e.target.value)}
            className={`w-full rounded-lg border border-slate-200 py-2.5 ${prefix ? "pl-7" : "pl-3"} pr-3 focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition`}
          />
        </div>
      </div>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-3 accent-[#c6a15b]"
      />
    </div>
  );

  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div className="rounded-2xl bg-white p-8 ring-1 ring-slate-100 shadow-sm space-y-6">
        <h3 className="font-display font-bold text-xl text-[#0b1f3a] flex items-center gap-2">
          <Calculator className="h-5 w-5 text-[#c6a15b]" /> Loan Payment Calculator
        </h3>
        <NumberField label="Loan Amount" value={amount} onChange={setAmount} min={CALC.AMOUNT_MIN} max={CALC.AMOUNT_MAX} step={CALC.AMOUNT_STEP} prefix="$" />
        <NumberField label="Interest Rate" value={rate} onChange={setRate} min={CALC.RATE_MIN} max={CALC.RATE_MAX} step={CALC.RATE_STEP} suffix="%" />
        <NumberField label="Term (Years)" value={years} onChange={setYears} min={CALC.YEARS_MIN} max={CALC.YEARS_MAX} step={CALC.YEARS_STEP} suffix=" yrs" />
        <p className="text-xs text-slate-400">
          Estimates only — not an offer to lend. Your actual rate and payment vary by program, credit, and property.
        </p>
      </div>

      <div className="rounded-2xl bg-[#0b1f3a] p-8 text-white flex flex-col justify-center">
        <div className="text-sm text-slate-300">Estimated Monthly Payment</div>
        <div className="font-display text-5xl font-extrabold text-[#c6a15b] mt-2">{currency(monthly)}</div>
        <div className="mt-8 space-y-4">
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <span className="flex items-center gap-2 text-slate-300"><DollarSign className="h-4 w-4 text-[#c6a15b]" /> Principal</span>
            <span className="font-semibold">{currency(Number(amount))}</span>
          </div>
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <span className="flex items-center gap-2 text-slate-300"><Percent className="h-4 w-4 text-[#c6a15b]" /> Total Interest</span>
            <span className="font-semibold">{currency(totalInterest)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-slate-300"><DollarSign className="h-4 w-4 text-[#c6a15b]" /> Total of Payments</span>
            <span className="font-semibold">{currency(totalPaid)}</span>
          </div>
        </div>
        <Link to="/apply" className="mt-8 inline-flex items-center justify-center gap-2 rounded-md bg-[#c6a15b] px-6 py-3 font-bold text-[#0b1f3a] hover:bg-[#d8bc85] transition-colors">
          Get My Real Rate <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

function BorrowerGuide() {
  return (
    <div className="max-w-3xl">
      <h3 className="font-display font-bold text-2xl text-[#0b1f3a]">Your Path to Funding</h3>
      <p className="mt-3 text-slate-600">A simple, transparent process from first conversation to funded loan.</p>
      <div className="mt-8 space-y-5">
        {buyerGuideSteps.map((s) => (
          <div key={s.step} className="flex gap-5 rounded-2xl bg-white p-6 ring-1 ring-slate-100 shadow-sm">
            <div className="h-11 w-11 rounded-xl bg-[#0b1f3a] text-[#c6a15b] font-display font-extrabold flex items-center justify-center shrink-0">
              {s.step}
            </div>
            <div>
              <h4 className="font-display font-bold text-[#0b1f3a]">{s.title}</h4>
              <p className="mt-1 text-slate-600 text-sm leading-relaxed">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <Link to="/apply" className="mt-8 inline-flex items-center gap-2 rounded-md bg-[#c6a15b] px-6 py-3 font-bold text-[#0b1f3a] hover:bg-[#d8bc85] transition-colors">
        Start Your Application <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function Glossary() {
  const [query, setQuery] = useState("");
  const filtered = glossary.filter(
    (g) => g.term.toLowerCase().includes(query.toLowerCase()) || g.def.toLowerCase().includes(query.toLowerCase())
  );
  return (
    <div>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search terms..."
          className="w-full rounded-lg border border-slate-200 pl-11 pr-4 py-3 focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition"
        />
      </div>
      <div className="mt-8 grid gap-5 md:grid-cols-2">
        {filtered.map((g) => (
          <div key={g.term} className="rounded-2xl bg-white p-6 ring-1 ring-slate-100 shadow-sm">
            <h4 className="font-display font-bold text-[#0b1f3a]">{g.term}</h4>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{g.def}</p>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="text-slate-500">No terms match "{query}".</p>
        )}
      </div>
    </div>
  );
}

function Documents() {
  return (
    <div>
      <h3 className="font-display font-bold text-2xl text-[#0b1f3a]">Documents Needed</h3>
      <p className="mt-3 text-slate-600 max-w-2xl">
        Having your paperwork ready speeds up approval. Requirements vary by program — your specialist
        will confirm exactly what applies to your loan.
      </p>
      <div className="mt-8 grid gap-6 md:grid-cols-2">
        {documentsChecklist.map((c) => (
          <div key={c.category} className="rounded-2xl bg-white p-6 ring-1 ring-slate-100 shadow-sm">
            <h4 className="font-display font-bold text-[#0b1f3a]">{c.category}</h4>
            <ul className="mt-4 space-y-3">
              {c.items.map((it) => (
                <li key={it} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="h-5 w-5 rounded-full bg-[#c6a15b]/15 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="h-3.5 w-3.5 text-[#b08e46]" />
                  </span>
                  {it}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Tools() {
  const [searchParams] = useSearchParams();
  const initialTab = tabs.some((t) => t.key === searchParams.get("tab"))
    ? searchParams.get("tab")
    : "calculators";
  const [tab, setTab] = useState(initialTab);
  return (
    <main>
      <PageHero
        title="Tools & Guides"
        crumb="Tools & Guides"
        subtitle="Everything you need to navigate the lending process — calculators, a plain-English glossary, document checklists, and a step-by-step borrower's guide."
      />

      <section className="py-16 bg-slate-50 min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold transition ${
                  tab === t.key ? "bg-[#0b1f3a] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"
                }`}
              >
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </div>

          <div className="mt-10">
            <Reveal key={tab}>
              {tab === "calculators" && <LoanCalculator />}
              {tab === "guide" && <BorrowerGuide />}
              {tab === "glossary" && <Glossary />}
              {tab === "documents" && <Documents />}
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0b1f3a]">Still have questions?</h2>
          <p className="mt-3 text-slate-600">Our lending specialists are here to help — no obligation, free consultation.</p>
          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/apply" className="inline-flex items-center justify-center gap-2 rounded-md bg-[#c6a15b] px-6 py-3 font-bold text-[#0b1f3a] hover:bg-[#d8bc85] transition-colors">
              Apply Now <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 px-6 py-3 font-semibold text-[#0b1f3a] hover:bg-slate-50 transition">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
